import consumer from "./consumer"
import { Dropdown } from "bootstrap"

// Centros de notificaciones del panel partner/staff. La campana del nav es un
// centro con su suscripción, su badge y sus rutas: dentro de una tienda, el de esa
// tienda (data-store-id); fuera, el global del partner. Badge = no-vistas (se apaga al abrir el
// panel), distintivo unread por item hasta el clic, borrado individual dinámico.
// Ver _docs/features/notifications/partner_notifications.md y notification_catalog.md

const subscriptions = new Map(); // campana (li) -> suscripción

function centers() {
  return Array.from(document.querySelectorAll(".partner-notifications-menu"));
}

function subscribe(center) {
  if (subscriptions.has(center)) return;

  const storeId = center.dataset.storeId;
  const identifier = storeId
    ? { channel: "StoreNotificationsChannel", store_id: storeId }
    : "PartnerNotificationsChannel";

  subscriptions.set(center, consumer.subscriptions.create(identifier, {
    // Sin `notification` el mensaje solo trae el contador (otro dispositivo de la
    // tienda ya vio los avisos): se sincroniza el badge y nada más.
    received(data) {
      if (data.notification) {
        onArrival(center, data);
      } else {
        updateBadge(center, data.unseen_count);
      }
    }
  }));
}

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : "";
}

function postJSON(path, options = {}) {
  return fetch(path, {
    method: options.method || "POST",
    headers: { "X-CSRF-Token": csrfToken() },
    keepalive: options.keepalive || false
  });
}

function bellLink(center) {
  return center.querySelector(".partner-notifications-link");
}

// Página de notificaciones del mismo centro que la campana (la global o la de esa
// tienda); null si se está en otra vista.
function pageOf(center) {
  const page = document.getElementById("partner-notifications-container");
  if (!page) return null;
  return (page.dataset.storeId || "") === (center.dataset.storeId || "") ? page : null;
}

// Centro al que pertenece un control: el de su campana o, en la página, el suyo.
function centerOf(element) {
  return element.closest(".partner-notifications-menu") || centers().find(pageOf) || null;
}

function panelIsOpen(center) {
  const panel = center.querySelector(".partner-notifications-menu__panel");
  return !!(panel && panel.classList.contains("show"));
}

// El toggle usa data-bs-auto-close="outside" para que la ✕ y "marcar todas" no
// cierren el panel; los clics que SÍ navegan lo cierran acá a mano (la campana
// global es turbo-permanent: sin esto el panel quedaría abierto sobre la página nueva).
function closePanel(center) {
  const dropdown = Dropdown.getInstance(bellLink(center));
  if (dropdown) dropdown.hide();
}

// ---------------------------------------------------------------------------
// Llegada en vivo (broadcast del PartnerNotificationService)
// ---------------------------------------------------------------------------

function onArrival(center, data) {
  const page = pageOf(center);

  prependToDropdown(center, data.dropdown_notification || data.notification);
  if (page) {
    prependToPage(page, data.notification);
    updateNotificationsCount(data.total_count);
  }

  // Si el usuario está mirando las notificaciones (panel abierto o página),
  // la llegada cuenta como vista al instante: no se enciende el badge.
  if (panelIsOpen(center) || page) {
    markAllAsSeen(center);
  } else {
    updateBadge(center, data.unseen_count);
  }
}

function prependToDropdown(center, html) {
  const menuList = center.querySelector(".partner-notifications-menu__list");
  if (!menuList || !html) return;

  const emptyState = menuList.querySelector(".partner-notifications-menu__empty");
  if (emptyState) emptyState.remove();
  menuList.insertAdjacentHTML("afterbegin", html);
}

function prependToPage(page, html) {
  if (!html) return;

  const emptyMessage = document.getElementById("no-notifications-message");
  if (emptyMessage) emptyMessage.remove();

  let list = document.getElementById("notifications-list");
  if (!list) {
    list = document.createElement("div");
    list.id = "notifications-list";
    page.prepend(list);
  }
  list.insertAdjacentHTML("afterbegin", html);
}

// ---------------------------------------------------------------------------
// Badge (no-vistas)
// ---------------------------------------------------------------------------

function updateBadge(center, unseenCount) {
  if (!unseenCount || unseenCount <= 0) {
    removeBadge(center);
    return;
  }

  let badge = center.querySelector(".partner-badge-notify");
  if (!badge) {
    badge = document.createElement("span");
    badge.className = "badge badge-number partner-badge-notify";
    bellLink(center).appendChild(badge);
  }
  badge.textContent = unseenCount;
}

function removeBadge(center) {
  const badge = center.querySelector(".partner-badge-notify");
  if (badge) badge.remove();
}

// ---------------------------------------------------------------------------
// Acciones contra el server
// ---------------------------------------------------------------------------

async function markAllAsSeen(center) {
  removeBadge(center);
  try {
    await postJSON(bellLink(center).dataset.markAllSeenPath);
  } catch (error) {
    console.error("Error al marcar notificaciones como vistas:", error);
  }
}

async function markAllAsRead(center) {
  clearAllUnreadStyles(center);
  removeBadge(center);
  try {
    await postJSON(bellLink(center).dataset.markAllReadPath);
  } catch (error) {
    console.error("Error al marcar notificaciones como leídas:", error);
  }
}

function markAsRead(readPath, notificationId, { keepalive } = {}) {
  clearUnreadStyles(notificationId);
  postJSON(readPath, { keepalive: !!keepalive }).catch((error) => {
    console.error("Error al marcar la notificación como leída:", error);
  });
}

async function deleteNotification(deletePath, notificationId) {
  try {
    const response = await postJSON(deletePath, { method: "DELETE" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json().catch(() => null);
    removeNotificationNodes(notificationId);
    updateNotificationsCount(data && data.total);
  } catch (error) {
    console.error("Error al eliminar la notificación:", error);
  }
}

// Sincroniza el "N notificaciones" del header de la página (si está renderizado)
// con el total real del server tras un borrado o una llegada en vivo.
function updateNotificationsCount(total) {
  if (total === null || total === undefined) return;
  const counter = document.getElementById("notifications-count");
  if (!counter) return;

  if (total <= 0) {
    counter.remove();
  } else {
    counter.textContent = `${total} ${total === 1 ? "notificación" : "notificaciones"}`;
  }
}

// ---------------------------------------------------------------------------
// Manipulación del DOM (la misma notificación puede estar en dropdown Y página)
// ---------------------------------------------------------------------------

const UNREAD_NODES = ".partner-notifications-menu__item.is-unread, .notification-card.unread";

function notificationNodes(notificationId) {
  return document.querySelectorAll(
    `.partner-notifications-menu__item[data-notification-id="${notificationId}"], ` +
    `.notification-card[data-notification-id="${notificationId}"]`
  );
}

function clearUnreadStyles(notificationId) {
  notificationNodes(notificationId).forEach((node) => {
    node.classList.remove("is-unread", "unread");
  });
}

// Solo los items de ese centro: su panel y, si corresponde, su página.
function clearAllUnreadStyles(center) {
  [center, pageOf(center)].filter(Boolean).forEach((scope) => {
    scope.querySelectorAll(UNREAD_NODES).forEach((node) => node.classList.remove("is-unread", "unread"));
  });
}

function removeNotificationNodes(notificationId) {
  notificationNodes(notificationId).forEach((node) => node.remove());
  centers().forEach(restoreMenuEmptyState);
  restorePageEmptyState();
}

function restoreMenuEmptyState(center) {
  const menuList = center.querySelector(".partner-notifications-menu__list");
  if (!menuList) return;
  if (menuList.querySelector(".partner-notifications-menu__item, .partner-notifications-menu__empty")) return;

  const template = center.querySelector("template");
  if (template) menuList.appendChild(template.content.cloneNode(true));
}

function restorePageEmptyState() {
  const list = document.getElementById("notifications-list");
  if (!list || list.querySelector(".notification-card")) return;

  const container = document.getElementById("partner-notifications-container");
  const template = document.getElementById("notifications-empty-template");
  if (container && template && !document.getElementById("no-notifications-message")) {
    container.appendChild(template.content.cloneNode(true));
  }
  list.remove();

  const loadMore = document.getElementById("load-more-notifications");
  if (loadMore) loadMore.remove();
  const headerActions = document.getElementById("notifications-header-actions");
  if (headerActions) headerActions.remove();
  const counter = document.getElementById("notifications-count");
  if (counter) counter.remove();
}

// ---------------------------------------------------------------------------
// Delegación de clicks: cubre los dropdowns, las cards de la página y los nodos
// insertados en vivo, sin re-attachear listeners por elemento.
// ---------------------------------------------------------------------------

function handleDelegatedClick(event) {
  const dismissButton = event.target.closest("[data-notification-dismiss]");
  if (dismissButton) {
    event.preventDefault();
    event.stopPropagation();
    deleteNotification(dismissButton.dataset.deletePath, dismissButton.dataset.notificationId);
    return;
  }

  const markReadButton = event.target.closest("[data-notification-mark-read]");
  if (markReadButton) {
    event.preventDefault();
    markAsRead(markReadButton.dataset.readPath, markReadButton.dataset.notificationId);
    return;
  }

  const markAllButton = event.target.closest("[data-notifications-mark-all-read]");
  if (markAllButton) {
    event.preventDefault();
    const center = centerOf(markAllButton);
    if (center) markAllAsRead(center);
    return;
  }

  // Clic en el cuerpo de una notificación: se marca leída y la navegación a su
  // URL sigue su curso (keepalive garantiza que el POST sobreviva al visit).
  const clickable = event.target.closest("[data-notification-click]");
  if (clickable) {
    const wrapper = clickable.closest("[data-notification-id]");
    if (wrapper) {
      markAsRead(clickable.dataset.readPath, wrapper.dataset.notificationId, { keepalive: true });
    }
  }

  // Cualquier link real dentro de un panel (cuerpo con URL, "Ver todas") navega:
  // cerramos el dropdown que auto-close="outside" dejó abierto.
  const panelAnchor = event.target.closest(".partner-notifications-menu__panel a[href]");
  if (panelAnchor) {
    closePanel(panelAnchor.closest(".partner-notifications-menu"));
  }
}

if (!window.__partnerNotificationsDelegated) {
  window.__partnerNotificationsDelegated = true;
  document.addEventListener("click", handleDelegatedClick);
}

// ---------------------------------------------------------------------------
// Ciclo de vida Turbo
// ---------------------------------------------------------------------------

document.addEventListener("turbo:load", () => {
  centers().forEach((center) => {
    subscribe(center);

    // La campana global es turbo-permanent: al visitar la página el server marca
    // todo como visto, pero el badge viejo persiste en el DOM y hay que apagarlo.
    if (pageOf(center)) {
      removeBadge(center);
    }

    // Red de seguridad para navegaciones no cubiertas por el click handler
    // (back/forward, restore): un panel que sobrevivió el visit se cierra acá.
    if (panelIsOpen(center)) {
      closePanel(center);
    }

    const link = bellLink(center);
    if (link.dataset.dropdownListenerAttached !== "true") {
      link.addEventListener("show.bs.dropdown", () => markAllAsSeen(center));
      link.dataset.dropdownListenerAttached = "true";
    }
  });
});

document.addEventListener("turbo:before-render", () => {
  subscriptions.forEach((subscription) => subscription.unsubscribe());
  subscriptions.clear();
});
